import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { FooterComponent } from '../../shared/components/footer/footer.component';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { RegisterService } from '../../core/services/register.service';
import { AuthService } from '../../core/services/auth.service';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { ToastService } from '../../shared/services/toast.service';
import { HeaderService } from '../../shared/services/header.service';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { UserFacade } from '../../core/store/user/user.facade';
import { RegisterMovelFlowService } from '../../core/services/register-movel-flow.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { CnpjLookupService } from '../../core/services/cnpj-lookup.service';
import { formatCnpj, unformatCnpj } from '../../core/utils/cnpj.util';

interface CNPJAccount {
  cnpj: string;
  companyName: string;
  tradeName: string;
  status: string;
  billingAccount: string;
  groupId: string;
  creationDate: string;
  lastUpdate: string;
  address?: {
    street?: string;
    number?: string;
    details?: string;
    district?: string;
    zip?: string;
    state?: string;
    city?: string;
  };
}

interface CNPJResponse {
  data: CNPJAccount[];
  meta: {
    totalItems: number;
    timestamp: string;
  };
}

@Component({
  selector: 'umkt-register-movel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    FooterComponent,
    AuthLayoutComponent,
    ConfirmModalComponent,
    I18nPipe
  ],
  templateUrl: './register-movel.component.html',
  styleUrls: [
    '../../../assets/style/login-register.component.scss',
    './register-movel.component.scss'
  ],
})
export class RegisterMovelComponent implements OnInit {
  
  searchForm!: FormGroup;
  searchTerm: string = '';

  cnpjAccounts: CNPJAccount[] = [];
  filteredCnpjAccounts: CNPJAccount[] = [];
  selectedCnpj: CNPJAccount | null = null;
  loading = false;
  showLogoutConfirm = false;

  get hasMoreThanFourItems(): boolean {
    return this.filteredCnpjAccounts.length > 4;
  }
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private registerService = inject(RegisterService);
  private authService = inject(AuthService);
  private cartFacade = inject(CartFacade);
  private toastService = inject(ToastService);
  private headerService = inject(HeaderService);
  private userFacade = inject(UserFacade);
  private registerMovelFlow = inject(RegisterMovelFlowService);
  private i18nService = inject(I18nService);
  private cnpjLookupService = inject(CnpjLookupService);


  ngOnInit(): void {
    if (this.registerMovelFlow.getStage() !== 'step2') {
      this.registerMovelFlow.setStage('cnpj-selection');
    }

    this.initializeForm();
    this.loadCnpjData();
  }

  private initializeForm(): void {
    this.searchForm = this.fb.group({
      search: ['', []]
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredCnpjAccounts = [...this.cnpjAccounts];
    this.selectedCnpj = null;
  }

  onSearchChange(event: any): void {
    const inputValue = event.target.value;
    let filteredValue = inputValue.replace(/[^A-Za-z0-9./-]/g, '');
    filteredValue = filteredValue.trimStart().replace(/\s+/g, '');
    filteredValue = filteredValue.replace(/^[./-]+/g, '');

    const clean = unformatCnpj(filteredValue);
    if (clean.length > 14) {
      filteredValue = formatCnpj(clean.slice(0, 14));
    } else if (filteredValue.includes('.') || filteredValue.includes('/') || filteredValue.includes('-')) {
      if (filteredValue.length > 18) {
        filteredValue = formatCnpj(clean);
      }
    } else if (clean.length === 14) {
      filteredValue = formatCnpj(clean);
    } else {
      filteredValue = clean;
    }

    if (inputValue !== filteredValue) {
      event.target.value = filteredValue;
    }

    this.searchTerm = filteredValue;
    this.filterCnpjAccounts(filteredValue);
    
    if (this.filteredCnpjAccounts.length === 1 && this.isCnpjSearch(filteredValue)) {
      this.selectedCnpj = this.filteredCnpjAccounts[0];
    } else {
      this.selectedCnpj = null;
    }
  }

  onKeyPress(event: KeyboardEvent): boolean {
    const char = event.key;
    const input = event.target as HTMLInputElement;
    const currentValue = input.value || '';
    
    const allowedChars = /[A-Za-z0-9./-]/;
    
    const specialKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];
    
    if (specialKeys.includes(char) || event.ctrlKey || event.metaKey) {
      return true;
    }
    
    if (char === ' ' || char === 'Space') {
      event.preventDefault();
      return false;
    }
    
    if ((char === '.' || char === '/' || char === '-') && currentValue.length === 0) {
      event.preventDefault();
      return false;
    }
    
    if (!allowedChars.test(char)) {
      event.preventDefault();
      return false;
    }
    
    const cleanChars = unformatCnpj(currentValue);
    const isAlnum = /[A-Za-z0-9]/.test(char);
    const isFormatChar = /[./-]/.test(char);
    
    if (isAlnum && cleanChars.length >= 14) {
      event.preventDefault();
      return false;
    }
    
    if (isFormatChar && currentValue.length >= 18) {
      event.preventDefault();
      return false;
    }
    
    if (isAlnum && (currentValue.includes('.') || currentValue.includes('/') || currentValue.includes('-'))) {
      if (currentValue.length >= 18) {
        event.preventDefault();
        return false;
      }
    }
    
    return true;
  }

  private isCnpjSearch(searchTerm: string): boolean {
    const cleanTerm = unformatCnpj(searchTerm);
    return cleanTerm.length >= 8; 
  }

  private loadCnpjData(): void {
    this.loading = true;
    
    this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
      const resources = loginData?.resources;
      
      if (!resources || resources.length === 0) {
        console.error('Lista de CNPJs não encontrada no store');
        this.loading = false;
        return;
      }

      // Verificar se são recursos da fixa (objetos) ou CNPJs (strings)
      // Este componente é específico para registro móvel, então só processa CNPJs
      const firstResource = resources[0];
      const isFixaMode = firstResource && typeof firstResource === 'object' && 'customerAccountId' in firstResource;
      
      if (isFixaMode) {
        console.warn('Este componente não suporta recursos da fixa. Use apenas CNPJs para registro móvel.');
        this.loading = false;
        return;
      }

      // Garantir que resources são strings (CNPJs)
      const cnpjList = resources as string[];

      const cnpjAccounts: CNPJAccount[] = cnpjList.map(cnpj => ({
        cnpj: this.formatCNPJ(cnpj),
        companyName: '',
        tradeName: '',
        status: 'ACTIVE',
        billingAccount: '',
        groupId: '',
        creationDate: new Date().toISOString(),
        lastUpdate: new Date().toISOString()
      }));

      const cnpjQueries = cnpjList.map(cnpj => 
        this.fetchCNPJInfo(cnpj).pipe(
          catchError(error => {
            console.error(`Erro ao buscar CNPJ ${cnpj}:`, error);
            return of(null);
          })
        )
      );

      forkJoin(cnpjQueries).subscribe(results => {
        results.forEach((cnpjData, index) => {
          if (cnpjData && cnpjData.company) {
            
            cnpjAccounts[index].companyName = cnpjData.company.name || cnpjAccounts[index].cnpj;
            cnpjAccounts[index].tradeName = cnpjData.alias || cnpjData.company.name || '';
            
            if (cnpjData.address) {
              cnpjAccounts[index].address = {
                street: cnpjData.address.street || '',
                number: cnpjData.address.number || '',
                details: cnpjData.address.details || '',
                district: cnpjData.address.district || '',
                zip: cnpjData.address.zip || '',
                state: cnpjData.address.state || '',
                city: cnpjData.address.city || ''
              };
            }
          } else {
            cnpjAccounts[index].companyName = cnpjAccounts[index].cnpj;
          }
        });

        this.cnpjAccounts = cnpjAccounts;
        this.filteredCnpjAccounts = this.cnpjAccounts;
        this.loading = false;
      });
    });
  }

  private formatCNPJ(cnpj: string): string {
    return formatCnpj(cnpj);
  }

  private cleanCNPJ(cnpj: string): string {
    return this.registerService.cleanDocument(cnpj);
  }

  private fetchCNPJInfo(cnpj: string) {
    const cleanCNPJ = this.registerService.cleanDocument(cnpj);
    return this.cnpjLookupService.fetchOffice(cleanCNPJ);
  }

  private filterCnpjAccounts(searchTerm: string): void {
    
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredCnpjAccounts = [...this.cnpjAccounts];
    } else {
      const term = searchTerm.toLowerCase().trim();
      
      this.filteredCnpjAccounts = this.cnpjAccounts.filter(account => {
        const cnpjMatch = account.cnpj.toLowerCase().includes(term);
        
        const cleanTerm = unformatCnpj(term).toLowerCase();
        const cleanCnpj = unformatCnpj(account.cnpj).toLowerCase();
        const cleanCnpjMatch = cleanTerm.length > 0 && cleanCnpj.includes(cleanTerm);
        
        const companyMatch = account.companyName.toLowerCase().includes(term) ||
                           account.tradeName.toLowerCase().includes(term);
        
        const result = cnpjMatch || cleanCnpjMatch || companyMatch;
        return result;
      });
    }
    
  }

  selectCnpj(cnpjAccount: CNPJAccount): void {
    this.selectedCnpj = cnpjAccount;
    
    const cleanCnpj = this.cleanCNPJ(cnpjAccount.cnpj);
    
    this.authService.syncSelectedDocument(cleanCnpj);
    
    this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
      const isFirstAccess = loginData?.isFirstAccess ?? false;
      
      const redirectAfterLogin = typeof sessionStorage !== 'undefined' 
        ? sessionStorage.getItem('redirect_after_login') 
        : null;
      
      if (!isFirstAccess) {
        this.registerMovelFlow.reset();
        
        if (redirectAfterLogin) {
          sessionStorage.removeItem('redirect_after_login');
          this.router.navigate([redirectAfterLogin]);
        } else {
          this.router.navigate(['/']);
        }
      } else {
        this.registerMovelFlow.setStage('step2');
        this.registerMovelFlow.clearSelectedAddress();
        this.router.navigate(['/register'], { queryParams: { step: 2, returnTo: 'register-movel' } });
      }
    });
  }

  goBack(): void {
    this.registerMovelFlow.reset();
    this.router.navigate(['/login']);
  }

  openLogoutConfirm(): void {
    this.showLogoutConfirm = true;
  }

  handleLogoutConfirm(): void {
    this.showLogoutConfirm = false;
    this.logout();
  }

  handleLogoutCancel(): void {
    this.showLogoutConfirm = false;
  }

  private logout(): void {
    this.authService.logout();
    
    this.cartFacade.clearCart();
    
    this.headerService.resetActiveTopNavItem();
    this.registerMovelFlow.reset();
    
    this.toastService.showSuccess(
      this.i18nService.instant('pages.registerMovel.messages.logoutSuccess'),
      3000,
      'top-center'
    );
    
    this.router.navigate(['/login']);
  }

  trackByCnpj(index: number, cnpj: CNPJAccount): string {
    return cnpj.cnpj;
  }
}
